import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import useLeadScoringConfigQuery from "app/pages/sales-intelligence/modules/lead-scoring-agent/query/queries/useLeadScoringConfigQuery";
import {
    findScoringRuleById,
    findScoringRuleScope,
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringBoardUtils";
import { filterAccountRulesByTeamMembership } from "app/pages/sales-intelligence/domain/leads/utils/filterAccountRulesByTeamMembership";
import { getPlaybookScoringConfigType } from "app/pages/sales-intelligence/modules/playbooks/utils/getPlaybookScoringConfigType";
import { useUserTeamMembership } from "app/pages/sales-intelligence/modules/teams/hooks/useUserTeamMembership";
import { ScoringConfigType } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

export const usePlaybookScoringContext = (isSharedWithAccount: boolean, scoringRuleId: string | null) => {
    const { translate } = useTranslate();
    const configType = getPlaybookScoringConfigType(isSharedWithAccount);

    const { data: userScoringConfig, isFetching: isUserFetching } = useLeadScoringConfigQuery(ScoringConfigType.User, {
        enabled: !isSharedWithAccount,
    });
    const { data: accountScoringConfig, isFetching: isAccountFetching } = useLeadScoringConfigQuery(
        ScoringConfigType.Account,
    );

    const { userTeamIds, isMembershipSuccess, isMembershipLoading } = useUserTeamMembership({
        enabled: !isSharedWithAccount,
    });

    const scoringConfig = isSharedWithAccount ? accountScoringConfig : userScoringConfig;
    const isScoringConfigLoading = isSharedWithAccount
        ? isAccountFetching && accountScoringConfig === undefined
        : (isUserFetching && userScoringConfig === undefined) ||
          (isAccountFetching && accountScoringConfig === undefined) ||
          isMembershipLoading;

    const userRules = userScoringConfig?.rules ?? {};
    const accountRules = accountScoringConfig?.rules ?? {};

    const applicableAccountRules = useMemo(() => {
        if (isSharedWithAccount) {
            return accountRules;
        }

        if (!isMembershipSuccess) {
            return accountRules;
        }

        return filterAccountRulesByTeamMembership(accountRules, userTeamIds);
    }, [accountRules, isMembershipSuccess, isSharedWithAccount, userTeamIds]);

    const selectedRule = useMemo(() => {
        if (!scoringRuleId) {
            return undefined;
        }

        if (isSharedWithAccount) {
            return findScoringRuleById(accountRules, scoringRuleId);
        }

        return (
            findScoringRuleById(userRules, scoringRuleId) ?? findScoringRuleById(applicableAccountRules, scoringRuleId)
        );
    }, [accountRules, applicableAccountRules, isSharedWithAccount, scoringRuleId, userRules]);

    const selectedRuleScope = useMemo(() => {
        if (!scoringRuleId || !selectedRule) {
            return undefined;
        }

        if (isSharedWithAccount) {
            return ScoringConfigType.Account;
        }

        return findScoringRuleScope(userRules, applicableAccountRules, scoringRuleId);
    }, [applicableAccountRules, isSharedWithAccount, scoringRuleId, selectedRule, userRules]);

    const isScoringRuleValid = !!scoringRuleId && (isScoringConfigLoading || !!selectedRule);

    const scoreCriteriaLabel =
        selectedRule?.businessContext?.translation ?? translate("si.playbooks.wizard.summary.not_set");

    return {
        configType,
        scoringConfig,
        isScoringConfigLoading,
        selectedRule,
        selectedRuleScope,
        isScoringRuleValid,
        scoreCriteriaLabel,
    };
};
